import React, { useCallback, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Popper, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { LinkIcon } from '../../../../shared/icons';
import { getChatRadii, getChatBorders } from '../chatStyles';

// Full names in the popover, because there is room for them there.
export const CONNECTOR_SURFACE_NAMES = {
  powerpoint: 'PowerPoint',
  excel: 'Excel',
  word: 'Word',
};

const SURFACE_ORDER = ['powerpoint', 'excel', 'word'];

const POPOVER_ID = 'connector-status-popover';

/**
 * ConnectorStatusIndicator - the cross-app link state as ONE header icon.
 *
 * Replaces the composer pill that spelled out "Excel . Word" on the bottom
 * row. The status itself stays readable without any interaction: the dot on
 * the icon is green when a sibling pane is actually reachable and muted when
 * it is not, which is the entire job of a status light. The names of what is
 * connected are detail, so they move into a popover.
 *
 * The popover opens on hover AND on keyboard focus, and Escape closes it. It
 * is not a hover-only control: this pane runs inside Office, where pointer
 * behaviour varies by host and platform, and a keyboard user has to be able
 * to reach the same information.
 *
 * Activating the button keeps the old pill's behaviour: reconnect when
 * offline, re-sync the peer list when online, so the dot can never go stale.
 */
const ConnectorStatusIndicator = ({
  app = 'powerpoint',
  conductorOnline = false,
  conductorPeers = [],
  onConnectorClick = null,
}) => {
  const theme = useTheme();
  const chatRadii = getChatRadii(theme);
  const chatBorders = getChatBorders(theme);
  const anchorRef = useRef(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [busy, setBusy] = useState(false);

  const otherSurfaces = useMemo(
    () => SURFACE_ORDER.filter((surface) => surface !== app),
    [app]
  );
  const connectedSurfaces = useMemo(
    () => new Set((conductorPeers || []).map((peer) => peer?.surface).filter(Boolean)),
    [conductorPeers]
  );
  const linkedSurfaces = conductorOnline
    ? otherSurfaces.filter((surface) => connectedSurfaces.has(surface))
    : [];
  const anyLinked = linkedSurfaces.length > 0;

  // Deliberately NOT theme.palette.success.main: the taskpane palette maps
  // `success` to a monochrome neutral (dark grey) by design, so a status dot
  // has to use a real hue. Muted sage greens read calm.
  const linkedGreen = theme.palette.mode === 'dark' ? '#7DA98C' : '#4A7C59';
  const idleDot = alpha(theme.palette.text.disabled, 0.55);

  const statusLine = !conductorOnline
    ? 'Office tools are disconnected. Select to reconnect.'
    : anyLinked
      ? `Available now: ${linkedSurfaces.map((s) => CONNECTOR_SURFACE_NAMES[s]).join(' and ')}. Select to refresh.`
      : `Open Restack in ${otherSurfaces.map((s) => CONNECTOR_SURFACE_NAMES[s]).join(' or ')} to make those files available.`;

  const open = hovered || focused;

  const handleClick = useCallback(async () => {
    if (!onConnectorClick || busy) return;
    setBusy(true);
    try {
      await onConnectorClick();
    } catch {
      // The dot stays muted and the user can try again.
    }
    setBusy(false);
  }, [busy, onConnectorClick]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Escape' && focused) {
      // Close without leaving the control: the popover is supplementary, so
      // Escape should not cost the user their place in the header.
      setFocused(false);
      setHovered(false);
    }
  }, [focused]);

  return (
    <Box
      data-testid="connector-status"
      sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <IconButton
        ref={anchorRef}
        size="small"
        onClick={handleClick}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onKeyDown={handleKeyDown}
        aria-busy={busy}
        aria-expanded={open}
        aria-describedby={open ? POPOVER_ID : undefined}
        aria-label={`Connected files. ${statusLine}`}
        data-testid="connector-status-indicator"
        sx={{
          position: 'relative',
          color: 'text.secondary',
          '@keyframes connectorPulse': {
            '0%': { opacity: 1 },
            '50%': { opacity: 0.35 },
            '100%': { opacity: 1 },
          },
        }}
      >
        {/* The status dot rides ON the glyph, not in the button's padding, so
            it reads as this icon's state and stays legible with no hover, no
            click and no popover. */}
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <LinkIcon />
          <Box
            aria-hidden="true"
            sx={{
              position: 'absolute',
              right: -2,
              bottom: -1,
              width: 7,
              height: 7,
              borderRadius: chatRadii.circle,
              backgroundColor: anyLinked ? linkedGreen : idleDot,
              // Ringed in the header's own fill so the dot stays a distinct
              // shape wherever it lands on the glyph.
              border: `1.5px solid ${theme.palette.mode === 'dark'
                ? theme.palette.background.default
                : '#FAFAFA'}`,
              boxSizing: 'content-box',
              animation: busy && !anyLinked ? 'connectorPulse 0.9s ease-in-out infinite' : 'none',
            }}
          />
        </Box>
      </IconButton>

      <Popper
        // Popper's own root already carries role="tooltip"; the content below
        // must not declare a second one.
        id={POPOVER_ID}
        open={open && Boolean(anchorRef.current)}
        anchorEl={anchorRef.current}
        placement="bottom-end"
        modifiers={[
          { name: 'offset', options: { offset: [0, 6] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ]}
        sx={{ zIndex: 1500 }}
      >
        <Box
          sx={{
            minWidth: theme.spacing(21),
            maxWidth: theme.spacing(30),
            padding: theme.spacing(1, 1.25),
            borderRadius: chatRadii.card,
            border: chatBorders.resting,
            backgroundColor: theme.palette.background.paper,
            boxShadow: theme.shadows[4],
          }}
        >
          <Typography
            component="div"
            sx={{
              fontSize: theme.typography.pxToRem(11),
              fontWeight: theme.typography.fontWeightSemiBold || 600,
              color: 'text.primary',
              lineHeight: 1.4,
              marginBottom: theme.spacing(0.5),
            }}
          >
            Connected files
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(0.375) }}>
            {otherSurfaces.map((surface) => {
              const isLinked = linkedSurfaces.includes(surface);
              return (
                <Box
                  key={surface}
                  sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(0.75), minWidth: 0 }}
                >
                  <Box
                    aria-hidden="true"
                    sx={{
                      width: 6,
                      height: 6,
                      flexShrink: 0,
                      borderRadius: chatRadii.circle,
                      backgroundColor: isLinked ? linkedGreen : idleDot,
                    }}
                  />
                  <Typography
                    component="div"
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      fontSize: theme.typography.pxToRem(11.5),
                      color: isLinked ? 'text.primary' : 'text.secondary',
                      lineHeight: 1.4,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {CONNECTOR_SURFACE_NAMES[surface]}
                  </Typography>
                  <Typography
                    component="div"
                    sx={{
                      flexShrink: 0,
                      fontSize: theme.typography.pxToRem(10),
                      color: 'text.secondary',
                      opacity: 0.8,
                      lineHeight: 1.4,
                    }}
                  >
                    {isLinked ? 'Open' : 'Not open'}
                  </Typography>
                </Box>
              );
            })}
          </Box>

          <Typography
            component="div"
            sx={{
              marginTop: theme.spacing(0.625),
              fontSize: theme.typography.pxToRem(10.5),
              color: 'text.secondary',
              opacity: 0.85,
              lineHeight: 1.4,
            }}
          >
            {statusLine}
          </Typography>
        </Box>
      </Popper>
    </Box>
  );
};

export default ConnectorStatusIndicator;
